/**
 * Inspector script injected into page iframe srcdoc.
 * Communicates with the parent via postMessage (sandbox forbids same-origin).
 *
 * Two responsibilities:
 *  1. Click-to-edit section selector (mouse overlay + message protocol
 *     that drives the parent's pill list).
 *  2. Section-level patch applier — listens for
 *     xavior-patch-section / xavior-insert-section / xavior-remove-section
 *     messages from the parent and mutates the matching
 *     `[data-section-id]` subtree. This lets incremental edits avoid a
 *     full srcDoc reassign, eliminating the white-flash reflow on every
 *     user edit (bug-iframe-flash in docs/pages-diagnostic-2026-04.md).
 *
 * Security: the sandbox is `allow-scripts` without `allow-same-origin`, so
 * the iframe's own origin is the string 'null'. Parent messages arrive
 * with the parent's real origin, which this script does not know at inject
 * time. We therefore require messages to carry a per-render nonce that the
 * parent embeds into the injected script and uses on every postMessage.
 * An attacker that gets a reference to the iframe's `contentWindow` from
 * a different frame would have to guess the nonce to mutate the document.
 */
export function getInspectorScript(nonce?: string): string {
  // Nonce is per-render — if the caller passes one, the script will only
  // accept patch messages that carry the matching `n` field. If the caller
  // doesn't pass a nonce (older render paths), we accept all messages from
  // `window.parent` (the pre-commit-7 behavior).
  const literalNonce = typeof nonce === 'string' && nonce.length > 0 ? JSON.stringify(nonce) : 'null';
  return `<script>(function(){
  var overlay=null,tooltip=null,selected=new Set(),active=false;
  var N=${literalNonce};
  var style=document.createElement('style');
  style.textContent='.xavior-selected{outline:3px solid #6366f1 !important;outline-offset:2px;}';
  document.head.appendChild(style);

  function getSections(){
    var els=document.querySelectorAll('[data-section-id]');
    if(!els.length) els=document.querySelectorAll('section,header,footer,nav,article');
    return els;
  }
  function sectionInfo(el){
    var id=el.getAttribute('data-section-id')||el.tagName.toLowerCase()+'-'+Array.prototype.indexOf.call(el.parentNode.children,el);
    var label=el.getAttribute('data-section-label')||el.getAttribute('aria-label')||id;
    return {id:id,label:label,tagName:el.tagName};
  }
  function removeOverlay(){
    if(overlay){overlay.remove();overlay=null;}
    if(tooltip){tooltip.remove();tooltip=null;}
  }
  function showOverlay(el){
    removeOverlay();
    var r=el.getBoundingClientRect();
    overlay=document.createElement('div');
    overlay.style.cssText='position:absolute;pointer-events:none;background:rgba(99,102,241,0.12);border:2px solid rgba(99,102,241,0.5);z-index:99999;top:'+
      (r.top+window.scrollY)+'px;left:'+(r.left+window.scrollX)+'px;width:'+r.width+'px;height:'+r.height+'px;';
    document.body.appendChild(overlay);
    tooltip=document.createElement('div');
    var info=sectionInfo(el);
    tooltip.textContent=info.label;
    tooltip.style.cssText='position:absolute;z-index:100000;background:#4f46e5;color:#fff;font:12px/1.4 system-ui,sans-serif;padding:2px 8px;border-radius:4px;top:'+
      Math.max(0,(r.top+window.scrollY-24))+'px;left:'+(r.left+window.scrollX)+'px;pointer-events:none;';
    document.body.appendChild(tooltip);
  }
  function onHover(e){
    var el=e.target.closest('[data-section-id],section,header,footer,nav,article');
    if(el) showOverlay(el); else removeOverlay();
  }
  function onClick(e){
    // Let anchor links keep their native scroll-to-hash behavior. Without
    // this the inspector swallows every nav click and the page stops
    // scrolling when the user clicks "Hero" / "About" / "Contact" links.
    // External <a href="https://..."> is also allowed through — sandbox
    // rules still govern whether the navigation actually happens.
    if(e.target.closest && e.target.closest('a[href]')) return;
    e.preventDefault();e.stopPropagation();
    var el=e.target.closest('[data-section-id],section,header,footer,nav,article');
    if(!el) return;
    var info=sectionInfo(el);
    if(selected.has(info.id)){
      selected.delete(info.id);el.classList.remove('xavior-selected');
      parent.postMessage({type:'xavior-section-deselected',section:info},'*');
    } else {
      selected.add(info.id);el.classList.add('xavior-selected');
      parent.postMessage({type:'xavior-section-selected',section:info},'*');
    }
  }
  function activate(){
    active=true;selected.clear();
    var els=getSections();var list=[];
    els.forEach(function(el){list.push(sectionInfo(el));});
    parent.postMessage({type:'xavior-sections-discovered',sections:list},'*');
    document.addEventListener('mouseover',onHover,true);
    document.addEventListener('click',onClick,true);
  }
  function deactivate(){
    active=false;removeOverlay();
    document.removeEventListener('mouseover',onHover,true);
    document.removeEventListener('click',onClick,true);
    document.querySelectorAll('.xavior-selected').forEach(function(el){el.classList.remove('xavior-selected');});
    selected.clear();
  }
  function q(id){return document.querySelector('[data-section-id="'+String(id).replace(/\\\\/g,'\\\\\\\\').replace(/"/g,'\\\\"')+'"]');}
  function fail(id,reason){parent.postMessage({type:'xavior-patch-failed',sectionId:id||null,reason:reason,n:N},'*');}
  // Append root for insert-by-null: prefer an explicit marker the page may
  // have authored; fall back to document.body. Mirrors page-patch.ts's
  // getPatchRoot so parent-side and iframe-side stay in sync.
  function getAppendRoot(){return document.querySelector('[data-xavior-page-root]')||document.body;}
  // After insert/replace, schedule a rAF that strips the transient
  // data-xavior-stream-enter attribute from the newly-mounted section.
  // Removing the attribute in a separate paint triggers the CSS transition
  // defined in the parents safetyStyles. No-op if the attribute is already
  // gone (e.g. non-streamed patch).
  function scheduleEnterStrip(sectionId){
    requestAnimationFrame(function(){
      var el=q(sectionId);
      if(el && el.hasAttribute && el.hasAttribute('data-xavior-stream-enter')){
        el.removeAttribute('data-xavior-stream-enter');
      }
    });
  }
  function applyPatch(m){
    try{
      if(m.type==='xavior-patch-section'){
        var el=q(m.sectionId);
        if(!el){fail(m.sectionId,'section-not-found');return;}
        el.outerHTML=m.outerHTML;
        scheduleEnterStrip(m.sectionId);
      } else if(m.type==='xavior-insert-section'){
        if(m.beforeSectionId){
          var a=q(m.beforeSectionId);
          if(!a){fail(m.sectionId||null,'anchor-not-found');return;}
          a.insertAdjacentHTML('beforebegin',m.outerHTML);
          if(m.sectionId) scheduleEnterStrip(m.sectionId);
        } else {
          var root=getAppendRoot();
          if(!root){fail(m.sectionId||null,'no-body');return;}
          root.insertAdjacentHTML('beforeend',m.outerHTML);
          if(m.sectionId) scheduleEnterStrip(m.sectionId);
        }
      } else if(m.type==='xavior-remove-section'){
        var r=q(m.sectionId);
        if(!r){fail(m.sectionId,'section-not-found');return;}
        r.remove();
      }
    } catch(err){
      fail(m && m.sectionId||null,'exception:'+(err && err.message||'unknown'));
    }
  }
  function externalDeselect(id){
    if(!active) return;
    if(!selected.has(id)) return;
    selected.delete(id);
    var el=q(id);
    if(el) el.classList.remove('xavior-selected');
  }
  function externalClearAll(){
    if(!active) return;
    selected.clear();
    document.querySelectorAll('.xavior-selected').forEach(function(el){el.classList.remove('xavior-selected');});
  }
  window.addEventListener('message',function(e){
    // Source gate: patch messages must come from the parent window. String
    // control messages (inspect-on/off) also originate from the parent,
    // but we keep the legacy no-check behavior for those to avoid
    // regressing click-to-edit in older entry points.
    if(e.data==='xavior-inspect-on'){activate();return;}
    if(e.data==='xavior-inspect-off'){deactivate();return;}
    if(e.data==='xavior-inspect-clear'){externalClearAll();return;}
    if(!e.data||typeof e.data!=='object'||typeof e.data.type!=='string') return;
    if(e.source!==window.parent) return;
    if(N!==null && e.data.n!==N) return;
    if(e.data.type==='xavior-patch-section'||e.data.type==='xavior-insert-section'||e.data.type==='xavior-remove-section'){
      applyPatch(e.data);
    } else if(e.data.type==='xavior-inspect-deselect'){
      // Parent-driven deselect (chip × in ChatBox) — clear highlight
      // inside the iframe without toggling the whole inspect mode.
      if(typeof e.data.sectionId==='string') externalDeselect(e.data.sectionId);
    }
    // xavior-full-reload is a parent-side concern — the parent reassigns
    // srcDoc, which tears down this iframe. Nothing to do here.
  });
})();<\/script>`;
}
